/**
 * Image Generation Service
 *
 * Handles all image generation operations including:
 * - Front image generation
 * - Back image generation
 * - Image modifications
 * - Uploaded image processing
 */

import { fetchWithAuthRetry } from "@/utils/auth-helpers";
import { processImageFile } from "@/utils/imageConverter";
import { logger } from "@/utils/logger";
import { DEFAULT_UPLOAD_PROMPT } from "@/utils/constants";
import { trackDressDesignEvent } from "@/utils/analytics";
import { normalizeValidationDetails } from "@/lib/apiValidationDetails";
import type { GenerationFailureCode } from "@/types/agentEvent";
import type { GenerateImagePromptPayload } from "@/services/generateImagePromptPayload";
import { designSpecSchema, type DesignSpec } from "@/types/designSpec";
import { projectDesignSpecToLegacyFormData } from "@/lib/agent/designSpec";
import {
  getUploadedImageReferences,
  expandShortModificationPrompt,
} from "@/lib/imageGenerationInputs";
import {
  parseGenerationCreditRecovery,
  type GenerationCreditRecovery,
} from "@/lib/generationFailureUx";

/**
 * Result of an image generation operation
 */
/** Extracted style attributes from upload reference images (populate form state) */
export interface ExtractedFormAttributes {
  sleeve?: string;
  length?: string;
  neckline?: string;
  silhouette?: string;
  fabric?: string;
  color?: string;
}

export interface GenerationResult {
  variations: Array<{
    imageUrl: string;
    imagePath: string | null;
    ticketId?: string;
  }>;
  warnings?: Array<{ variation: number; reason: string; error?: string }>;
  partialFailure?: boolean;
  /** Opaque one-time capability for the automatic agent image-review turn. */
  imageReviewToken?: string;
  /** From upload flow: style attributes extracted from reference images */
  extractedAttributes?: ExtractedFormAttributes;
  /** Exact validated contract the server used and persisted for this render. */
  designSpec?: DesignSpec;
  /** Server-authoritative hint that this response spent the visitor's free render. */
}

function parseResponseDesignSpec(json: unknown): DesignSpec | undefined {
  if (!json || typeof json !== "object" || !("design_spec" in json)) {
    return undefined;
  }
  const value = (json as { design_spec?: unknown }).design_spec;
  if (value === undefined || value === null) return undefined;
  const parsed = designSpecSchema.safeParse(value);
  if (!parsed.success) {
    throw new ServerError(
      "The image was generated, but its design version could not be verified. Please try again."
    );
  }
  return parsed.data;
}

/**
 * Custom error classes for better error handling
 */
/**
 * Which wall a 402 should raise.
 *
 * The legacy "anonymous_free_render_spent" token identifies a guest credit
 * gate. Guests now receive a bonus only after signup, never during rendering.
 * Keep accepting this token for compatibility with already-open clients.
 */
export type CreditWallKind =
  | "insufficient_credits"
  | "anonymous_free_render_spent";

export class InsufficientCreditsError extends Error {
  readonly wall: CreditWallKind;

  constructor(
    message: string = "Insufficient credits",
    wall: CreditWallKind = "insufficient_credits"
  ) {
    super(message);
    this.name = "InsufficientCreditsError";
    this.wall = wall;
  }
}

export class RateLimitError extends Error {
  retryAfter?: number;
  constructor(message: string = "Rate limit exceeded", retryAfter?: number) {
    super(message);
    this.name = "RateLimitError";
    this.retryAfter = retryAfter;
  }
}

export class GenerationInProgressError extends Error {
  constructor(message: string = "A generation is already in progress") {
    super(message);
    this.name = "GenerationInProgressError";
  }
}

export type ValidationErrorField = "prompt" | "design_spec" | null;

function validationFieldFor(details: readonly string[]): ValidationErrorField {
  if (details.some(detail => /^prompt(?:\.|:)/i.test(detail))) {
    return "prompt";
  }
  if (details.some(detail => /^design_spec(?:\.|:)/i.test(detail))) {
    return "design_spec";
  }
  return null;
}

function actionableValidationMessage(
  fallback: string,
  details: readonly string[]
): string {
  const detail = details[0];
  if (!detail) return fallback;
  return detail.replace(/^(?:prompt|design_spec)(?:\.[^:]+)?:\s*/i, "");
}

export class ValidationError extends Error {
  readonly details: string[];
  readonly field: ValidationErrorField;

  constructor(message: string = "Invalid request", details?: unknown) {
    const normalizedDetails = normalizeValidationDetails(details);
    super(actionableValidationMessage(message, normalizedDetails));
    this.name = "ValidationError";
    this.details = normalizedDetails;
    this.field = validationFieldFor(normalizedDetails);
  }
}

export function isPromptValidationError(
  error: unknown
): error is ValidationError {
  return error instanceof ValidationError && error.field === "prompt";
}

export class ServerError extends Error {
  errorDetails?: string;
  creditRecovery?: GenerationCreditRecovery;
  constructor(
    message: string = "Server error",
    errorDetails?: string,
    creditRecovery?: GenerationCreditRecovery
  ) {
    super(message);
    this.name = "ServerError";
    this.errorDetails = errorDetails;
    this.creditRecovery = creditRecovery;
  }
}

type GenerationOperation = "front" | "back" | "modify";

interface PendingGenerationIntent {
  fingerprint: string;
  generationId: string;
}

interface GenerationApiResponse {
  response: Response;
  json: any;
  jsonParsed: boolean;
}

/**
 * A stable identity for the failures that `classifyGenerationFailure` can only
 * call "unknown".
 *
 * The generation paths throw bare `new Error(...)` in nineteen places, and all
 * nineteen land in the "unknown" bucket. The beacon stores no message text by
 * design — closed vocabulary, no caller-authored strings — so 62 of the 143
 * client-side failures recorded since 2026-08-28 were undiagnosable: we knew
 * something broke and could not say what. A code on the error closes that gap
 * without sending free text, and works for the interpolated messages a
 * message-matching lookup could never catch.
 *
 * `error_class` still says what KIND of failure it was; this says WHICH one.
 */

/**
 * An ordinary Error that also carries a closed-vocabulary code.
 *
 * Deliberately not a new class per failure: the user-facing `message` and any
 * `cause` are passed straight through unchanged, so this is invisible to every
 * existing catch, error-display and `instanceof` check. It only adds something
 * for telemetry to read.
 */
export class CodedGenerationError extends Error {
  readonly code: GenerationFailureCode;

  constructor(
    code: GenerationFailureCode,
    message: string,
    options?: ErrorOptions
  ) {
    super(message, options);
    this.name = "CodedGenerationError";
    this.code = code;
  }
}

export function generationFailureCode(
  error: unknown
): GenerationFailureCode | undefined {
  return error instanceof CodedGenerationError ? error.code : undefined;
}

export type GenerationFailureClass =
  | "validation"
  | "insufficient_credits"
  | "rate_limited"
  | "generation_in_progress"
  | "server_error"
  | "network"
  | "unknown";

function classifyGenerationFailure(error: unknown): GenerationFailureClass {
  if (error instanceof ValidationError) return "validation";
  if (error instanceof InsufficientCreditsError) return "insufficient_credits";
  if (error instanceof RateLimitError) return "rate_limited";
  if (error instanceof GenerationInProgressError) {
    return "generation_in_progress";
  }
  if (error instanceof ServerError) return "server_error";
  // A transport failure surfaces as TypeError ("Failed to fetch"), and an
  // aborted request as AbortError; both mean the render never happened.
  if (
    error instanceof TypeError ||
    (error instanceof Error && error.name === "AbortError")
  ) {
    return "network";
  }
  return "unknown";
}

/**
 * Failure classes /api/generate-image records for itself.
 *
 * Its 402 and 5xx paths both run `recordGenerationFailure`, which emits a
 * `generation_failed` row of its own. The beacon carries no shared idempotency
 * key, so beaconing those too would roughly double the count for the one class
 * that was already instrumented — the opposite of making the column readable.
 * Every other class (a 429/409 the route rejects before emitting, a request
 * that never arrived, one the browser refused to send) has no server row at
 * all, and the beacon is its only trace.
 */
const SERVER_RECORDED_FAILURE_CLASSES: ReadonlySet<GenerationFailureClass> =
  new Set<GenerationFailureClass>(["insufficient_credits", "server_error"]);

/**
 * Beacons a failed render to the server so it lands in `agent_events`.
 *
 * /api/generate-image records every failure that reaches its turn, but a
 * request rejected before the turn opens — or one that never leaves the
 * browser at all — used to leave no trace anywhere, which is why
 * `generation_failed` had no history to read. Fire-and-forget in every sense:
 * it is never awaited and can never throw into the generation path.
 */
function reportGenerationFailure(params: {
  operation: GenerationOperation;
  agentSessionId?: string | null;
  error: unknown;
}): void {
  const errorClass = classifyGenerationFailure(params.error);
  if (SERVER_RECORDED_FAILURE_CLASSES.has(errorClass)) return;
  const errorCode = generationFailureCode(params.error);
  try {
    void fetch("/api/create/agent/event", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: "generation_failed",
        operation: params.operation,
        error_class: errorClass,
        // Present only for the throws that carry one; still a closed
        // vocabulary, never the message text.
        ...(errorCode ? { error_code: errorCode } : {}),
        ...(params.agentSessionId ? { session_id: params.agentSessionId } : {}),
      }),
      keepalive: true,
    }).catch(() => {
      // Telemetry must never interfere with reporting the failure to the user.
    });
  } catch {
    // Same: a missing/blocked fetch is not a generation problem.
  }
}

async function withGenerationFailureTelemetry<T>(
  operation: GenerationOperation,
  agentSessionId: string | null | undefined,
  run: () => Promise<T>
): Promise<T> {
  try {
    return await run();
  } catch (error) {
    reportGenerationFailure({ operation, agentSessionId, error });
    throw error;
  }
}

const GENERATION_INTENT_STORAGE_PREFIX = "build-a-dress:generation-intent:v1";
const memoryGenerationIntents = new Map<string, PendingGenerationIntent>();
const memoryOnlyGenerationIntentKeys = new Set<string>();

function generationIntentStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function generationIntentKey(
  operation: GenerationOperation,
  fingerprint: string
): string {
  return `${GENERATION_INTENT_STORAGE_PREFIX}:${operation}:${fingerprint}`;
}

function readGenerationIntent(key: string): PendingGenerationIntent | null {
  const storage = generationIntentStorage();
  let serialized: string | null = null;
  if (storage) {
    try {
      serialized = storage.getItem(key);
      if (!serialized) {
        return memoryOnlyGenerationIntentKeys.has(key)
          ? (memoryGenerationIntents.get(key) ?? null)
          : null;
      }
    } catch {
      // Fall through to the in-memory fallback.
    }
  }

  if (!serialized) {
    return memoryGenerationIntents.get(key) ?? null;
  }

  try {
    const parsed = JSON.parse(serialized) as Partial<PendingGenerationIntent>;
    if (
      typeof parsed.fingerprint === "string" &&
      typeof parsed.generationId === "string"
    ) {
      return {
        fingerprint: parsed.fingerprint,
        generationId: parsed.generationId,
      };
    }
  } catch {
    // A malformed browser value is not authoritative.
  }

  try {
    storage?.removeItem(key);
  } catch {
    // Best-effort cleanup only.
  }
  return memoryGenerationIntents.get(key) ?? null;
}

function writeGenerationIntent(
  key: string,
  intent: PendingGenerationIntent
): void {
  const storage = generationIntentStorage();
  try {
    if (storage) {
      storage.setItem(key, JSON.stringify(intent));
      memoryGenerationIntents.delete(key);
      memoryOnlyGenerationIntentKeys.delete(key);
      return;
    }
  } catch {
    // Private browsing and quota failures use the module-memory fallback.
  }
  memoryGenerationIntents.set(key, intent);
  memoryOnlyGenerationIntentKeys.add(key);
}

function clearGenerationIntent(
  key: string,
  intent: PendingGenerationIntent
): void {
  const current = readGenerationIntent(key);
  if (
    current?.fingerprint !== intent.fingerprint ||
    current.generationId !== intent.generationId
  ) {
    return;
  }

  memoryGenerationIntents.delete(key);
  memoryOnlyGenerationIntentKeys.delete(key);
  try {
    generationIntentStorage()?.removeItem(key);
  } catch {
    // Best-effort cleanup only.
  }
}

function fallbackRequestFingerprint(serialized: string): string {
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < serialized.length; index++) {
    const codePoint = serialized.charCodeAt(index);
    first = Math.imul(first ^ codePoint, 0x01000193);
    second = Math.imul(second ^ codePoint, 0x85ebca6b);
  }
  return `${serialized.length.toString(36)}-${(first >>> 0).toString(36)}-${(
    second >>> 0
  ).toString(36)}`;
}

async function requestFingerprint(
  requestBody: Record<string, unknown>
): Promise<string> {
  const payloadWithoutGenerationId = { ...requestBody };
  delete payloadWithoutGenerationId.generation_id;
  const serialized = JSON.stringify(payloadWithoutGenerationId);

  try {
    if (globalThis.crypto?.subtle && typeof TextEncoder !== "undefined") {
      const digest = await globalThis.crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(serialized)
      );
      return Array.from(new Uint8Array(digest))
        .map(value => value.toString(16).padStart(2, "0"))
        .join("");
    }
  } catch {
    // Older browsers and test environments use the deterministic fallback.
  }

  return fallbackRequestFingerprint(serialized);
}

async function resolveGenerationIntent(
  operation: GenerationOperation,
  requestBody: Record<string, unknown>,
  explicitGenerationId?: string
): Promise<{
  key: string;
  intent: PendingGenerationIntent;
  serializedBody: string;
}> {
  const fingerprint = await requestFingerprint(requestBody);
  const key = generationIntentKey(operation, fingerprint);
  const storedIntent = readGenerationIntent(key);
  const matchingStoredIntent =
    storedIntent?.fingerprint === fingerprint ? storedIntent : null;
  const generatedId =
    typeof requestBody.generation_id === "string"
      ? requestBody.generation_id
      : crypto.randomUUID();
  const generationId =
    explicitGenerationId || matchingStoredIntent?.generationId || generatedId;
  const intent = { fingerprint, generationId };

  requestBody.generation_id = generationId;
  writeGenerationIntent(key, intent);

  return {
    key,
    intent,
    // Serialize exactly once so an automatic retry cannot drift from the
    // original body (including its generation UUID).
    serializedBody: JSON.stringify(requestBody),
  };
}

async function executeGenerationRequest(
  operation: GenerationOperation,
  requestBody: Record<string, unknown>,
  explicitGenerationId?: string
): Promise<GenerationApiResponse> {
  const { key, intent, serializedBody } = await resolveGenerationIntent(
    operation,
    requestBody,
    explicitGenerationId
  );
  const requestInit: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: serializedBody,
  };

  let response: Response | null = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      // This layer owns the single idempotent retry. Disable the helper's
      // nested transport loop so one browser action can issue at most two
      // requests with the same generation UUID.
      response = await fetchWithAuthRetry(
        "/api/generate-image",
        requestInit,
        0,
        0
      );
    } catch (error) {
      if (attempt === 1) {
        // The request may have committed before its response was lost. Keep
        // the intent so a manual retry replays the same generation UUID.
        throw error;
      }
      logger.warn("Retrying image generation after a network failure", {
        component: "ImageGenerationService",
        metadata: { operation, retryAttempt: attempt + 1 },
      });
      continue;
    }

    if ((response.status >= 500 || response.status === 401) && attempt === 0) {
      logger.warn("Retrying image generation after a transient response", {
        component: "ImageGenerationService",
        metadata: {
          operation,
          retryAttempt: attempt + 1,
          responseStatus: response.status,
        },
      });
      continue;
    }
    break;
  }

  if (!response) {
    throw new CodedGenerationError(
      "no_response",
      "Image generation did not return a response"
    );
  }

  let json: any;
  let jsonParsed = false;
  try {
    json = await response.json();
    jsonParsed = true;
  } catch {
    // A lost or malformed success response is ambiguous, as is a non-JSON
    // gateway 5xx. Keeping the intent makes a manual retry idempotent.
  }

  const isUnambiguousTerminalResponse =
    (response.ok && jsonParsed) ||
    (!response.ok &&
      response.status !== 409 &&
      (response.status < 500 || jsonParsed));
  if (isUnambiguousTerminalResponse) {
    clearGenerationIntent(key, intent);
  }

  return { response, json, jsonParsed };
}

/**
 * Converts a File to base64 string
 * @param file - The file to convert
 * @returns Promise<string> - Base64 encoded string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  try {
    logger.debug("Converting file to base64", {
      component: "ImageGenerationService",
      metadata: {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      },
    });

    // Process the image (convert JPG/JPEG to PNG if needed)
    const processedFile = await processImageFile(file);

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data:image/*;base64, prefix to get just the base64 data
        const base64Data = result.split(",")[1];

        logger.debug("File converted to base64", {
          component: "ImageGenerationService",
          metadata: {
            originalFileName: file.name,
            processedFileName: processedFile.name,
            originalType: file.type,
            processedType: processedFile.type,
            base64Length: base64Data.length,
          },
        });

        resolve(base64Data);
      };
      reader.onerror = error => {
        logger.error(
          "Error reading file as data URL",
          {
            component: "ImageGenerationService",
            metadata: {
              fileName: processedFile.name,
              fileType: processedFile.type,
            },
          },
          new Error(`FileReader error: ${error?.type || "unknown"}`)
        );
        reject(new Error("Failed to read file as data URL"));
      };
      reader.readAsDataURL(processedFile);
    });
  } catch (error) {
    logger.error(
      "Error processing image file",
      {
        component: "ImageGenerationService",
        metadata: { fileName: file.name, fileType: file.type },
      },
      error as Error
    );
    throw error;
  }
}

function extensionForMimeType(mimeType: string): string {
  switch (mimeType) {
    case "image/jpeg":
    case "image/jpg":
      return "jpg";
    case "image/webp":
      return "webp";
    case "image/heic":
      return "heic";
    case "image/heif":
      return "heif";
    case "image/png":
    default:
      return "png";
  }
}

async function dataUrlToBase64(dataUrl: string): Promise<string> {
  const response = await fetch(dataUrl);
  if (!response.ok) {
    throw new CodedGenerationError(
      "invalid_data_url",
      "Invalid image data URL"
    );
  }

  const blob = await response.blob();
  const mimeType =
    blob.type || dataUrl.match(/^data:([^;,]+)/)?.[1] || "image/png";
  const file = new File([blob], `reference.${extensionForMimeType(mimeType)}`, {
    type: mimeType,
  });
  return fileToBase64(file);
}

/**
 * Handles error responses from the API
 */
function handleApiError(response: Response, errorData?: any): never {
  if (response.status === 402) {
    throw new InsufficientCreditsError(
      errorData?.error || "Insufficient credits",
      errorData?.credit_wall === "anonymous_free_render_spent"
        ? "anonymous_free_render_spent"
        : "insufficient_credits"
    );
  } else if (response.status === 429) {
    const retryAfter = errorData?.retryAfter
      ? Math.ceil(errorData.retryAfter / 1000)
      : 60;
    throw new RateLimitError(
      errorData?.error ||
        `Too many requests. Please wait a moment before trying again. Try again in ${retryAfter} seconds.`,
      retryAfter
    );
  } else if (response.status === 409) {
    throw new GenerationInProgressError(
      errorData?.error ||
        "A generation is already in progress. Please wait for it to complete."
    );
  } else if (response.status === 400) {
    throw new ValidationError(
      errorData?.error ||
        "Invalid request. Please check your prompt and try again.",
      errorData?.details
    );
  } else if (response.status >= 500) {
    throw new ServerError(
      errorData?.error || "Server error. Please try again in a few moments.",
      errorData?.error_details,
      parseGenerationCreditRecovery(errorData?.credit_recovery)
    );
  } else {
    throw new CodedGenerationError(
      "request_failed",
      errorData?.error || "Failed to generate image. Please try again."
    );
  }
}

/**
 * Parameters for front image generation
 */
const MODESTY_PROMPT_PREFIX: Record<
  "modest" | "balanced" | "revealing",
  string
> = {
  modest: "Modest style with full coverage, higher necklines, and sleeves. ",
  balanced: "",
  revealing: "Elegant style with lower neckline or strapless options. ",
};

const joinArrayOrString = (
  v: string | string[] | undefined
): string | undefined => {
  if (v === undefined) return undefined;
  if (typeof v === "string") return v.trim() || undefined;
  const joined = v
    .filter((s): s is string => typeof s === "string")
    .map(s => s.trim())
    .filter(Boolean)
    .join(", ");
  return joined || undefined;
};

export interface GenerateFrontImageParams {
  prompt: string;
  generationId?: string;
  agentSessionId?: string | null;
  designSpec?: DesignSpec;
  /** Preset type from form (Wedding, Formal, etc.) - prefer over prompt inference */
  preset_type?: string | null;
  /** Form state may have array from agent/preset flow; service joins with ", " for API */
  color: string | string[];
  colorHex?: string | string[] | null;
  length: string;
  sleeve: string;
  sleeveAddons: string[];
  neckline: string;
  waistline: string;
  silhouette: string;
  /** Form state may have array from agent/preset flow; service joins with ", " for API */
  fabric: string | string[];
  isModificationMode?: boolean;
  uploadedImages?: File[];
  uploadedImageUrls?: string[];
  isUploadPreset?: boolean;
  enableCrazyMode?: boolean;
  originalPrompt?: string; // For prompt aggregation in modifications
  modestyPreference?: "modest" | "balanced" | "revealing";
}

export async function buildFrontImageRequestBody(
  params: GenerateFrontImageParams
): Promise<Record<string, unknown>> {
  const {
    prompt,
    generationId,
    agentSessionId,
    designSpec,
    preset_type,
    color,
    colorHex,
    length,
    sleeve,
    sleeveAddons,
    neckline,
    waistline,
    silhouette,
    fabric,
    isModificationMode = false,
    uploadedImages,
    uploadedImageUrls,
    isUploadPreset = false,
    enableCrazyMode = false,
    originalPrompt,
    modestyPreference,
  } = params;

  const uploadedImageReferences = getUploadedImageReferences(
    uploadedImages,
    uploadedImageUrls,
    designSpec,
    "front"
  );
  const uploadReferenceCount = uploadedImageReferences?.length ?? 0;
  const isFrontUpload = uploadReferenceCount > 0 || isUploadPreset;
  const basePrompt =
    prompt.trim().length >= 10
      ? prompt.trim()
      : isFrontUpload
        ? DEFAULT_UPLOAD_PROMPT
        : prompt.trim();
  const modestyPrefix =
    modestyPreference && modestyPreference !== "balanced"
      ? MODESTY_PROMPT_PREFIX[modestyPreference]
      : "";
  const contractLegacy = designSpec
    ? projectDesignSpecToLegacyFormData(designSpec)
    : undefined;
  const effectiveFrontPrompt = contractLegacy
    ? contractLegacy.prompt
    : modestyPrefix + basePrompt;

  const fabricStr = contractLegacy
    ? joinArrayOrString(contractLegacy.fabric)
    : joinArrayOrString(fabric);
  const colorStr = contractLegacy
    ? joinArrayOrString(contractLegacy.color)
    : joinArrayOrString(color);
  const colorHexStr = contractLegacy
    ? undefined
    : joinArrayOrString(colorHex ?? undefined);

  const requestBody: Record<string, unknown> = {
    prompt: effectiveFrontPrompt,
    generation_id: generationId ?? crypto.randomUUID(),
    // Sent unconditionally, null included: the conditional spread dropped the
    // key whenever the client had not resolved a session yet, leaving the
    // server with nothing to distinguish "no session" from "never sent" and
    // nothing to alert on. An explicit null lets the route fall back.
    agent_session_id: agentSessionId ?? null,
    ...(designSpec && { design_spec: designSpec }),
    preset_type: contractLegacy
      ? contractLegacy.preset_type || undefined
      : preset_type || undefined,
    color: colorStr ?? "",
    color_hex: colorHexStr ?? undefined,
    length: contractLegacy?.length ?? length,
    sleeve: contractLegacy?.sleeve ?? sleeve,
    sleeveAddons: contractLegacy?.sleeveAddons ?? sleeveAddons,
    neckline: contractLegacy?.neckline ?? neckline,
    waistline: contractLegacy?.waistline ?? waistline,
    silhouette: contractLegacy?.silhouette ?? silhouette,
    fabric: fabricStr ?? "",
    create_ticket: false,
    is_modification: isModificationMode,
    enable_crazy_mode: enableCrazyMode,
  };

  if (isModificationMode && originalPrompt) {
    requestBody.original_prompt = originalPrompt.trim();
  }

  if (uploadedImageReferences && uploadReferenceCount > 0) {
    try {
      requestBody.uploaded_images_data = await filesOrUrlsToBase64(
        uploadedImageReferences
      );
      requestBody.is_upload = true;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to process images. Please try different images.";
      logger.error(
        "Failed to process uploaded front images",
        {
          component: "ImageGenerationService",
          metadata: {
            imageCount: uploadReferenceCount,
          },
        },
        error as Error
      );
      throw new CodedGenerationError(
        "image_processing_failed",
        `Image processing failed: ${errorMessage}. Please try different images.`
      );
    }
  }

  return requestBody;
}

/**
 * Generates front image variations
 */
export async function generateFrontImage(
  params: GenerateFrontImageParams
): Promise<GenerationResult> {
  return withGenerationFailureTelemetry("front", params.agentSessionId, () =>
    runFrontImageGeneration(params)
  );
}

async function runFrontImageGeneration(
  params: GenerateFrontImageParams
): Promise<GenerationResult> {
  const requestBody = await buildFrontImageRequestBody(params);
  const effectiveFrontPrompt = String(requestBody.prompt || "");

  // Track dress_design_start when API call is initiated
  trackDressDesignEvent(
    "start",
    effectiveFrontPrompt,
    2, // step 2 for front
    "front"
  );

  const { response, json, jsonParsed } = await executeGenerationRequest(
    "front",
    requestBody,
    params.generationId
  );

  if (!response.ok) {
    handleApiError(response, json);
  }

  if (!jsonParsed) {
    throw new ServerError(
      "The image was generated, but its response could not be read. Please try again."
    );
  }

  // Log raw API response ticket IDs
  logger.info("Received API response for front image generation", {
    component: "ImageGenerationService",
    metadata: {
      variationCount: json.variations?.length || 0,
      ticketIdsInResponse:
        json.variations?.map((v: any, i: number) => ({
          index: i,
          ticketId: v.ticket_id,
          hasTicketId: !!v.ticket_id,
          imageUrl: v.image_url ? "present" : "missing",
        })) || [],
    },
  });

  // Handle variations array response
  if (!json?.variations || json.variations.length === 0) {
    throw new CodedGenerationError(
      "no_variations",
      "No variations generated. Please try again."
    );
  }

  // Convert variations to result format
  const variations = json.variations.map((v: any) => ({
    imageUrl: v.image_url,
    imagePath: v.image_path || null,
    ticketId: v.ticket_id || undefined,
  }));

  // Track dress_design_complete when images are successfully returned
  trackDressDesignEvent(
    "complete",
    effectiveFrontPrompt,
    2, // step 2 for front
    "front"
  );

  return {
    variations,
    warnings: json.warnings || [],
    partialFailure: json.partial_failure === true,
    imageReviewToken: json.image_review_token || undefined,
    extractedAttributes: json.extracted_attributes,
    designSpec: parseResponseDesignSpec(json),
  };
}

/**
 * Parameters for back image generation
 */
export interface GenerateBackImageParams {
  backPrompt: string;
  generationId?: string;
  baseImagePath: string;
  agentSessionId?: string | null;
  designSpec?: DesignSpec;
  isModificationMode?: boolean;
  uploadedImages?: File[];
  uploadedImageUrls?: string[];
  isUploadPreset?: boolean;
  originalBackPrompt?: string; // For prompt aggregation in modifications
  frontPrompt?: string; // Front prompt for back ticket creation (p_prompt field)
  modestyPreference?: "modest" | "balanced" | "revealing";
  /** Form data for ticket creation—ensures design fields are saved when front ticket lacks them */
  formData?: {
    length?: string;
    sleeve?: string;
    sleeveAddons?: string[];
    neckline?: string;
    waistline?: string;
    silhouette?: string;
    fabric?: string | string[];
    color?: string | string[];
    colorHex?: string | string[] | null;
    preset_type?: string | null;
  };
}

export async function buildBackImageRequestBody(
  params: GenerateBackImageParams
): Promise<Record<string, unknown>> {
  const {
    backPrompt,
    generationId,
    baseImagePath,
    agentSessionId,
    designSpec,
    isModificationMode = false,
    uploadedImages,
    uploadedImageUrls,
    isUploadPreset = false,
    originalBackPrompt,
    frontPrompt,
    modestyPreference,
    formData: formDataParam,
  } = params;

  if (!baseImagePath) {
    throw new CodedGenerationError(
      "missing_front_image",
      "Front image not found. Please start over."
    );
  }

  const uploadedImageReferences = getUploadedImageReferences(
    uploadedImages,
    uploadedImageUrls,
    designSpec,
    "back"
  );
  const uploadReferenceCount = uploadedImageReferences?.length ?? 0;
  const isBackUpload = uploadReferenceCount > 0 || isUploadPreset;
  const contractLegacy = designSpec
    ? projectDesignSpecToLegacyFormData(designSpec)
    : undefined;
  const requestedBackPrompt = contractLegacy
    ? contractLegacy.backPrompt ||
      `Back view preserving ${contractLegacy.prompt}`
    : backPrompt;
  const hasValidBackPrompt =
    !!requestedBackPrompt && requestedBackPrompt.trim().length >= 10;
  const shouldGenerateBack = hasValidBackPrompt || uploadReferenceCount > 0;

  if (!shouldGenerateBack) {
    throw new CodedGenerationError(
      "missing_back_prompt",
      "Please provide a back design prompt."
    );
  }

  const baseBackPrompt = hasValidBackPrompt
    ? requestedBackPrompt.trim()
    : isBackUpload
      ? DEFAULT_UPLOAD_PROMPT
      : requestedBackPrompt.trim();
  const modestyPrefix =
    modestyPreference && modestyPreference !== "balanced"
      ? MODESTY_PROMPT_PREFIX[modestyPreference]
      : "";
  const effectiveBackPrompt = contractLegacy
    ? baseBackPrompt
    : modestyPrefix + baseBackPrompt;

  const requestBody: Record<string, unknown> = {
    prompt: effectiveBackPrompt,
    generation_id: generationId ?? crypto.randomUUID(),
    // Sent unconditionally, null included: the conditional spread dropped the
    // key whenever the client had not resolved a session yet, leaving the
    // server with nothing to distinguish "no session" from "never sent" and
    // nothing to alert on. An explicit null lets the route fall back.
    agent_session_id: agentSessionId ?? null,
    ...(designSpec && { design_spec: designSpec }),
    base_image_url: baseImagePath,
    is_modification: isModificationMode,
    create_ticket: false,
    generate_back_view: true,
    is_sequential_back: true,
    back_prompt: effectiveBackPrompt,
  };

  if (contractLegacy) {
    requestBody.front_prompt = contractLegacy.prompt;
    requestBody.length = contractLegacy.length;
    requestBody.sleeve = contractLegacy.sleeve;
    requestBody.sleeveAddons = contractLegacy.sleeveAddons;
    requestBody.neckline = contractLegacy.neckline;
    requestBody.waistline = contractLegacy.waistline;
    requestBody.silhouette = contractLegacy.silhouette;
    requestBody.fabric = joinArrayOrString(contractLegacy.fabric) ?? "";
    requestBody.color = joinArrayOrString(contractLegacy.color) ?? "";
    requestBody.preset_type = contractLegacy.preset_type || undefined;
  } else if (frontPrompt) {
    requestBody.front_prompt = frontPrompt.trim();
  }

  if (!contractLegacy && formDataParam) {
    if (formDataParam.length) requestBody.length = formDataParam.length;
    if (formDataParam.sleeve) requestBody.sleeve = formDataParam.sleeve;
    if (formDataParam.sleeveAddons?.length) {
      requestBody.sleeveAddons = formDataParam.sleeveAddons;
    }
    if (formDataParam.neckline) requestBody.neckline = formDataParam.neckline;
    if (formDataParam.waistline) {
      requestBody.waistline = formDataParam.waistline;
    }
    if (formDataParam.silhouette) {
      requestBody.silhouette = formDataParam.silhouette;
    }
    if (formDataParam.fabric) {
      requestBody.fabric = joinArrayOrString(formDataParam.fabric);
    }
    if (formDataParam.color) {
      requestBody.color = joinArrayOrString(formDataParam.color);
    }
    if (formDataParam.colorHex) {
      requestBody.color_hex = joinArrayOrString(formDataParam.colorHex);
    }
    if (formDataParam.preset_type) {
      requestBody.preset_type = formDataParam.preset_type;
    }
  }

  if (
    isModificationMode &&
    (contractLegacy ? requestedBackPrompt : originalBackPrompt)
  ) {
    requestBody.original_back_prompt = (
      contractLegacy ? requestedBackPrompt : originalBackPrompt!
    ).trim();
  }

  if (isModificationMode && (contractLegacy?.prompt || frontPrompt)) {
    requestBody.original_prompt = (
      contractLegacy?.prompt ?? frontPrompt!
    ).trim();
  }

  if (uploadedImageReferences && uploadReferenceCount > 0) {
    try {
      requestBody.uploaded_images_data = await filesOrUrlsToBase64(
        uploadedImageReferences
      );
      requestBody.is_upload = true;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to process images. Please try different images.";
      logger.error(
        "Failed to process uploaded back images",
        {
          component: "ImageGenerationService",
          metadata: {
            imageCount: uploadReferenceCount,
          },
        },
        error as Error
      );
      throw new CodedGenerationError(
        "image_processing_failed",
        `Image processing failed: ${errorMessage}. Please try different images.`
      );
    }
  }

  return requestBody;
}

/**
 * Generates back image variations
 */
export async function generateBackImage(
  params: GenerateBackImageParams
): Promise<GenerationResult> {
  return withGenerationFailureTelemetry("back", params.agentSessionId, () =>
    runBackImageGeneration(params)
  );
}

async function runBackImageGeneration(
  params: GenerateBackImageParams
): Promise<GenerationResult> {
  const requestBody = await buildBackImageRequestBody(params);
  const effectiveBackPrompt = String(requestBody.prompt || "");

  // Track dress_design_start when API call is initiated
  trackDressDesignEvent(
    "start",
    effectiveBackPrompt,
    4, // step 4 for back
    "back"
  );

  const { response, json, jsonParsed } = await executeGenerationRequest(
    "back",
    requestBody,
    params.generationId
  );

  if (!response.ok) {
    handleApiError(response, json);
  }

  if (!jsonParsed) {
    throw new ServerError(
      "The image was generated, but its response could not be read. Please try again."
    );
  }

  // Handle variations array response
  if (!json?.variations || json.variations.length === 0) {
    throw new CodedGenerationError(
      "no_variations",
      "No variations generated. Please try again."
    );
  }

  // Convert variations to result format
  const variations = json.variations.map((v: any) => ({
    imageUrl: v.image_url,
    imagePath: v.image_path || null,
    ticketId: v.ticket_id || undefined,
  }));

  // Track dress_design_complete when images are successfully returned
  trackDressDesignEvent(
    "complete",
    effectiveBackPrompt,
    4, // step 4 for back
    "back"
  );

  return {
    variations,
    warnings: json.warnings || [],
    partialFailure: json.partial_failure === true,
    imageReviewToken: json.image_review_token || undefined,
    designSpec: parseResponseDesignSpec(json),
  };
}

export interface InspectImagePromptResult {
  requestBody: Record<string, unknown>;
  promptPayload: GenerateImagePromptPayload;
  note?: string;
}

async function inspectImagePromptRequest(
  requestBody: Record<string, unknown>
): Promise<InspectImagePromptResult> {
  const response = await fetch("/api/generate-image/inspect-prompt", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  const json = await response.json().catch(() => null);
  if (!response.ok || !json?.success || !json.data) {
    throw new Error(json?.error || "Failed to inspect image prompt");
  }

  return json.data as InspectImagePromptResult;
}

export async function inspectFrontImagePrompt(
  params: GenerateFrontImageParams
): Promise<InspectImagePromptResult> {
  return inspectImagePromptRequest(await buildFrontImageRequestBody(params));
}

export async function inspectBackImagePrompt(
  params: GenerateBackImageParams
): Promise<InspectImagePromptResult> {
  return inspectImagePromptRequest(await buildBackImageRequestBody(params));
}

/**
 * Converts an array of Files or image URLs to base64 strings.
 * Used for passing reference images to generation/modification API.
 */
const REFERENCE_FETCH_TIMEOUT_MS = 15000;
const REFERENCE_FETCH_ATTEMPTS = 2;

/**
 * Downloads a remote reference image with a timeout and one retry.
 *
 * A raw `fetch()` that fails at the network/CORS/DNS layer throws
 * `TypeError: Failed to fetch`, which previously slipped past the `response.ok`
 * check and surfaced to users verbatim as "Image processing failed: Failed to
 * fetch. Please try different images." — blaming the user for a transient
 * network blip or an expired signed URL. This bounds each attempt with an
 * AbortController, retries once on a transient failure, and always throws a
 * single clear, non-leaky error so the caller can show actionable copy.
 */
async function downloadReferenceImage(url: string): Promise<Blob> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= REFERENCE_FETCH_ATTEMPTS; attempt++) {
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      REFERENCE_FETCH_TIMEOUT_MS
    );
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        // A completed HTTP response (e.g. 403 from an expired signed URL, 404).
        // Not retryable — the URL itself is bad.
        throw new CodedGenerationError(
          "reference_download_failed",
          `Failed to download uploaded image reference (status ${response.status})`
        );
      }
      return await response.blob();
    } catch (error) {
      lastError = error;
      const isAbort = error instanceof Error && error.name === "AbortError";
      const isNetwork =
        error instanceof TypeError || // fetch transport failure ("Failed to fetch")
        isAbort;
      // Only transport-level failures are worth retrying; a bad-status Error is not.
      if (!isNetwork || attempt === REFERENCE_FETCH_ATTEMPTS) {
        break;
      }
    } finally {
      clearTimeout(timeout);
    }
  }
  // Normalize everything to a single friendly message; never leak "Failed to fetch".
  throw new CodedGenerationError(
    "reference_download_failed",
    "Failed to download uploaded image reference",
    { cause: lastError }
  );
}

export async function filesOrUrlsToBase64(
  filesOrUrls: Array<File | string>
): Promise<string[]> {
  if (filesOrUrls.length === 0) return [];

  const convertOne = async (item: File | string): Promise<string> => {
    if (typeof item !== "string") {
      return fileToBase64(item);
    }
    if (item.startsWith("data:")) {
      if (!item.includes(",")) {
        throw new CodedGenerationError(
          "invalid_data_url",
          "Invalid image data URL"
        );
      }
      return dataUrlToBase64(item);
    }
    // item is a remote URL (e.g. a rehydrated Supabase reference image).
    const blob = await downloadReferenceImage(item);
    const file = new File([blob], "reference.png", {
      type: blob.type || "image/png",
    });
    return fileToBase64(file);
  };

  return Promise.all(filesOrUrls.map(convertOne));
}

/**
 * Parameters for image modification
 */
export interface ModifyImageParams {
  modificationPrompt: string;
  generationId?: string;
  baseImagePath: string;
  isModifyingFront: boolean;
  agentSessionId?: string | null;
  designSpec?: DesignSpec;
  originalPrompt?: string; // For front modifications
  originalBackPrompt?: string; // For back modifications
  preset_type?: string | null;
  color?: string | string[];
  colorHex?: string | string[] | null;
  length?: string;
  sleeve?: string;
  sleeveAddons?: string[];
  neckline?: string;
  waistline?: string;
  silhouette?: string;
  fabric?: string | string[];
  uploadedImages?: File[];
  uploadedImageUrls?: string[];
  /** Reference images as base64 (e.g. user uploads) so output matches reference */
  uploadedImagesData?: string[];
}

/**
 * Modifies an existing image with a new prompt
 */
export async function modifyImage(
  params: ModifyImageParams
): Promise<GenerationResult> {
  return withGenerationFailureTelemetry("modify", params.agentSessionId, () =>
    runImageModification(params)
  );
}

async function runImageModification(
  params: ModifyImageParams
): Promise<GenerationResult> {
  const {
    modificationPrompt,
    generationId,
    baseImagePath,
    isModifyingFront,
    agentSessionId,
    designSpec,
    originalPrompt,
    originalBackPrompt,
    preset_type,
    color,
    colorHex,
    length,
    sleeve,
    sleeveAddons,
    neckline,
    waistline,
    silhouette,
    fabric,
    uploadedImages,
    uploadedImageUrls,
    uploadedImagesData,
  } = params;

  const trimmedPrompt = modificationPrompt.trim();
  if (!trimmedPrompt) {
    throw new ValidationError("Please enter a modification description.");
  }

  if (!baseImagePath) {
    throw new CodedGenerationError(
      "design_save_failed",
      "Failed to save design. Please try again."
    );
  }

  const contractLegacy = designSpec
    ? projectDesignSpecToLegacyFormData(designSpec)
    : undefined;
  const colorStr = joinArrayOrString(contractLegacy?.color ?? color);
  const colorHexStr = contractLegacy
    ? undefined
    : joinArrayOrString(colorHex ?? undefined);
  const fabricStr = joinArrayOrString(contractLegacy?.fabric ?? fabric);
  const effectivePrompt = expandShortModificationPrompt(trimmedPrompt, {
    side: isModifyingFront ? "front" : "back",
    basePrompt: isModifyingFront
      ? (contractLegacy?.prompt ?? originalPrompt)
      : (contractLegacy?.backPrompt ?? originalBackPrompt),
    attributes: {
      color: colorStr,
      fabric: fabricStr,
      silhouette: contractLegacy?.silhouette ?? silhouette,
      neckline: contractLegacy?.neckline ?? neckline,
      length: contractLegacy?.length ?? length,
      sleeve: contractLegacy?.sleeve ?? sleeve,
    },
  });

  const requestBody: any = {
    prompt: effectivePrompt,
    generation_id: generationId ?? crypto.randomUUID(),
    // Sent unconditionally, null included: the conditional spread dropped the
    // key whenever the client had not resolved a session yet, leaving the
    // server with nothing to distinguish "no session" from "never sent" and
    // nothing to alert on. An explicit null lets the route fall back.
    agent_session_id: agentSessionId ?? null,
    ...(designSpec && { design_spec: designSpec }),
    base_image_url: baseImagePath,
    is_modification: true,
    create_ticket: false,
    generate_back_view: !isModifyingFront,
  };

  if (contractLegacy) {
    requestBody.preset_type = contractLegacy.preset_type || undefined;
    requestBody.color = colorStr ?? "";
    requestBody.length = contractLegacy.length;
    requestBody.sleeve = contractLegacy.sleeve;
    requestBody.sleeveAddons = contractLegacy.sleeveAddons;
    requestBody.neckline = contractLegacy.neckline;
    requestBody.waistline = contractLegacy.waistline;
    requestBody.silhouette = contractLegacy.silhouette;
    requestBody.fabric = fabricStr ?? "";
  } else {
    if (preset_type) requestBody.preset_type = preset_type;
    if (colorStr) requestBody.color = colorStr;
    if (colorHexStr) requestBody.color_hex = colorHexStr;
    if (length) requestBody.length = length;
    if (sleeve) requestBody.sleeve = sleeve;
    if (sleeveAddons?.length) requestBody.sleeveAddons = sleeveAddons;
    if (neckline) requestBody.neckline = neckline;
    if (waistline) requestBody.waistline = waistline;
    if (silhouette) requestBody.silhouette = silhouette;
    if (fabricStr) requestBody.fabric = fabricStr;
  }

  const uploadedImageReferences = getUploadedImageReferences(
    uploadedImages,
    uploadedImageUrls,
    designSpec,
    isModifyingFront ? "front" : "back"
  );
  const resolvedUploadedImagesData =
    uploadedImageReferences && uploadedImageReferences.length > 0
      ? await filesOrUrlsToBase64(uploadedImageReferences)
      : designSpec
        ? undefined
        : uploadedImagesData;
  if (resolvedUploadedImagesData && resolvedUploadedImagesData.length > 0) {
    requestBody.uploaded_images_data = resolvedUploadedImagesData;
  }

  // Add prompt aggregation fields
  if (isModifyingFront && (contractLegacy?.prompt || originalPrompt)) {
    requestBody.original_prompt = (
      contractLegacy?.prompt ?? originalPrompt!
    ).trim();
  } else if (!isModifyingFront) {
    // Back modification: pass back_prompt and original_back_prompt
    requestBody.back_prompt = effectivePrompt;
    const contractBackPrompt = contractLegacy
      ? contractLegacy.backPrompt ||
        `Back view preserving ${contractLegacy.prompt}`
      : undefined;
    if (contractBackPrompt || originalBackPrompt) {
      requestBody.original_back_prompt = (
        contractBackPrompt ?? originalBackPrompt!
      ).trim();
    }
  }

  const { response, json, jsonParsed } = await executeGenerationRequest(
    "modify",
    requestBody,
    params.generationId
  );

  if (!response.ok) {
    handleApiError(response, json);
  }

  if (!jsonParsed) {
    throw new ServerError(
      "The image was generated, but its response could not be read. Please try again."
    );
  }

  // Handle variations array response
  if (!json?.variations || json.variations.length === 0) {
    throw new CodedGenerationError(
      "no_variations",
      "No variations generated. Please try again."
    );
  }

  // Convert variations to result format
  const variations = json.variations.map((v: any) => ({
    imageUrl: v.image_url,
    imagePath: v.image_path || null,
    ticketId: v.ticket_id || undefined,
  }));

  return {
    variations,
    warnings: json.warnings || [],
    partialFailure: json.partial_failure === true,
    imageReviewToken: json.image_review_token || undefined,
    designSpec: parseResponseDesignSpec(json),
  };
}

/**
 * Parameters for prompt recovery from chat context
 */
export interface RecoverPromptParams {
  messages: Array<{
    role: "user" | "assistant";
    content: string | Record<string, unknown>;
  }>;
  formState?: Partial<{
    preset_type: string | null;
    color: string | string[] | null;
    length: string | null;
    sleeve: string | null;
    neckline: string | null;
    silhouette: string | null;
    fabric: string | string[] | null;
  }>;
  side?: "front" | "back";
}

/**
 * Recovers a valid image generation prompt from chat context when the original
 * prompt was too short or empty. Uses flash-lite for low latency/cost.
 * Call this when generation fails with ValidationError (400) due to prompt length.
 */
export async function recoverPromptFromChat(
  params: RecoverPromptParams
): Promise<string> {
  const { messages, formState = {}, side = "front" } = params;

  const response = await fetchWithAuthRetry("/api/create/recover-prompt", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      messages: messages.map(m => ({
        role: m.role,
        content: m.content,
      })),
      formState,
      side,
    }),
  });

  if (!response.ok) {
    let errorMessage = "Failed to recover prompt";
    try {
      const data = await response.json();
      errorMessage = data?.error || errorMessage;
    } catch {
      // Use default
    }
    throw new Error(errorMessage);
  }

  const json = await response.json();
  const prompt = json?.data?.prompt;

  if (typeof prompt !== "string" || prompt.trim().length < 10) {
    throw new Error("Recovered prompt is invalid");
  }

  logger.info("Prompt recovered from chat context", {
    component: "ImageGenerationService",
    metadata: {
      promptLength: prompt.length,
      messageCount: messages.length,
      side,
    },
  });

  return prompt.trim();
}

/**
 * Processes an uploaded image file to base64
 * @param file - The file to process
 * @returns Promise<string> - Base64 encoded string
 */
export async function processUploadedImage(file: File): Promise<string> {
  return fileToBase64(file);
}
